import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController } from '@ionic/angular/lazy';
import { OrderService } from 'src/app/services/order.service';
import { CourierService } from 'src/app/services/courier.service';
import moment from 'moment-timezone';
import { ConnectedAccountService } from 'src/app/services/connected-account.service';
import { StorageService } from 'src/app/services/storage.service';
import { DateTimePickerPage } from 'src/app/modals/date-time-picker/date-time-picker.page';
import { ObserverService } from 'src/app/services/observer.service';
import { UserService } from 'src/app/services/user.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-order-fulfillment-edit',
    templateUrl: './order-fulfillment-edit.page.html',
    styleUrls: ['./order-fulfillment-edit.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class OrderFulfillmentEditPage implements OnInit {

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    moment = moment;
    couriers = null;
    user = null;
    @Input() order_id = null;
    @Input() fulfillment_id = null;
    fulfillment = null;
    formated_date = null;
    date_modal_css_class = '';

    courier_options: any = { header: 'Courier' };

    params = {
        send_order_shipped_email: false
    };

    // modal flags
    date_picker_modal_open = false;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        private loadingController: LoadingController,
        private orderService: OrderService,
        private connectedAccountService: ConnectedAccountService,
        private storageService: StorageService,
        private courierService: CourierService,
        private observerService: ObserverService,
        private userService: UserService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.date_modal_css_class = 'backdrop';
        }

        if(this.fulfillment_id) {


            if(this.order_id) {


                this.loadData().then(() => {

                    if(this.fulfillment.date) {
                        this.params['date'] = this.moment.utc(this.fulfillment.date).tz(this.user.timezone.code).toISOString(true);
                        this.formated_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");
                    }
                    else {
                        this.params['date'] = "";
                    }
        
                    if(this.fulfillment.courier && this.fulfillment.courier.id) {
                        this.params["courier_id"] = this.fulfillment.courier.id;
                    }
                    else {
                        this.params["courier_id"] = "";
                    }
        
                    if(this.fulfillment.courier && this.fulfillment.courier.tracking_number) {
                        this.params["courier_tracking_number"] = this.fulfillment.courier.tracking_number;
                    } 
                    else {
                        this.params["courier_tracking_number"] = "";
                    }
        
                    if(this.fulfillment.customer_note) {
                        this.params["customer_note"] = this.fulfillment.customer_note;
                    } 
                    else {
                        this.params["customer_note"] = "";
                    }
        
                })
                .catch(() => { });

            }

        }

        this.observerService.subscribe('network_status_changed|order_fulfillment_edit_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to edit a fulfillment, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|order_fulfillment_edit_modal");
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading order fulfillment").then(() => {
                this.loadUser().then(() => {
                    this.loadCouriers().then(() => {
                        this.loadOrderFulfillment().then(() => {
                            this.loading_spinner.dismiss();
                            resolve();
                        })
                        .catch(() => {
                            this.loading_spinner.dismiss();
                            reject();
                        });
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    loadUser() {
        let promise = new Promise<void>((resolve, reject) => {
            this.user = null;
            this.storageService.get("connected_account_id").then((connected_account_id) => {
                if(connected_account_id) {
                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account: any) => {
                        if(connected_account['user_id']) {
                            this.userService.getUser(connected_account['user_id']).then((user: any) => {
                                this.user = user;
                            });
                        }
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
                }
            })
            .catch(() => {
                reject();
            });
        });
        return promise;
    }

    loadCouriers() {
        let promise = new Promise<void>((resolve, reject) => {
            this.couriers = null;
            this.courierService.getCouriers().then((couriers) => {
                if(couriers) {
                    this.couriers = couriers;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
        return promise;
    }

    loadOrderFulfillment() {
        let promise = new Promise<void>((resolve, reject) => {
            this.fulfillment = null;
            this.orderService.getOrderFulfillment(this.order_id, this.fulfillment_id).then((fulfillment) => {
                if(fulfillment) {
                    this.fulfillment = fulfillment;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
        return promise;
    }

    close() {
        this.modalController.dismiss();
    }

    clearCourier() {
        this.params["courier_id"] = "";
    }

    update() {

        let validated = true;

        if(this.params['date'] == null || this.params['date'].trim() === "") {
            validated = false;
            this.presentAlert("Missing date", "Please select a date");
        }

        if(validated) {
            this.params['date'] = this.moment(this.params['date']).utc().toISOString();
            
            this.updateOrderFulfillment().then(() => {
                this.modalController.dismiss();
            });

        }

    }

    updateOrderFulfillment() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Updating order fulfillment").then(() => {
                this.orderService.updateOrderFulfillment(this.order_id, this.fulfillment_id, this.params).then(() =>{
                    this.spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.spinner.dismiss();
                    reject();
                });
            });
        });
    }

    async presentDateTimePickerModal() {
        if(!this.date_picker_modal_open) {
            this.date_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: DateTimePickerPage,
                cssClass: this.date_modal_css_class,
                componentProps: {
                    date: (this.params['date'] ? this.params['date'] : ""),
                    format: "date-time",
                    clear_date: false
                }
            })
            modal.onDidDismiss().then((data) => {
                this.date_picker_modal_open = false;
                if(data.data) {
                    this.params['date'] = data.data['date'];
                    this.formated_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    } 

}
